/**
 * FacilityDemoOverlay — Full-screen demo overlay for AI Agent video recording
 *
 * Displays a full-screen swipeable card carousel:
 *   Card 1: Opens the real 3D model from the project (via existing model viewer)
 *   Card 2: SOP document with yellow-highlighted safety lines
 *   Card 3: Tutorial video auto-play
 *
 * Then transitions to a full-screen resolved state with PDF download.
 *
 * Uses position:fixed to cover the entire viewport (not just the avatar container).
 * Mobile-first responsive design.
 */

'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';

// ==================== TYPES ====================

interface SopLine {
  text: string;
  highlight: boolean;
}

interface FacilityDemoData {
  equipmentName: string;
  videoUrl: string;
  videoTitle: string;
  sopTitle: string;
  sopLines: SopLine[];
  modelOpened: boolean;
}

interface IncidentReportData {
  summary: string;
  timestamp: string;
  fileName: string;
  language?: string;
}

type OverlayState = 'hidden' | 'cards' | 'transitioning' | 'resolved';

// ==================== CONSTANTS ====================

const DEMO_VIDEO_URL =
  'https://s3.hcm-1.cloud.cmctelecom.vn/starglobal-3d/smart-facility-360/qtsc/upload/video2d/close_up_industrial_water_supp_gwr_video_mvp.mp4';

const AGENT_NAME = 'Libertie';

const DEFAULT_SOP_LINES: SopLine[] = [
  { text: 'Bước 1: Xác định vị trí van cần đóng trên sơ đồ P&ID', highlight: false },
  { text: '⚠️  Bước 2: BẮT BUỘC bấm chốt an toàn (Safety Pin) màu vàng', highlight: true },
  { text: 'Bước 3: Vặn tay quay theo chiều kim đồng hồ (CW) đến hết hành trình', highlight: false },
  { text: '⚠️  Bước 4: Kiểm tra đồng hồ áp suất = 0 bar', highlight: true },
  { text: 'Bước 5: Khóa van và gắn biển "LOCKED OUT – DO NOT OPERATE"', highlight: false },
  { text: 'Bước 6: Báo cáo cho Trưởng ca qua bộ đàm / hệ thống', highlight: false },
];

const DEFAULT_FACILITY_DATA: FacilityDemoData = {
  equipmentName: 'Hệ thống van cấp nước',
  videoUrl: DEMO_VIDEO_URL,
  videoTitle: 'Hướng dẫn đóng van thủ công',
  sopTitle: 'SOP-VLV-003: Quy trình đóng van thủ công khi sự cố',
  sopLines: DEFAULT_SOP_LINES,
  modelOpened: false,
};

// ==================== SANITIZATION HELPER ====================

function escapeHtml(str: string): string {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

// ==================== PDF GENERATION ====================

function generateIncidentPdfBlob(report: IncidentReportData): Blob {
  const isEn = report.language === 'en' || report.language === 'english';
  const dateStr = new Date(report.timestamp).toLocaleString(isEn ? 'en-US' : 'vi-VN', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });

  const title = isEn ? "INCIDENT REPORT" : "BIÊN BẢN BÁO CÁO SỰ CỐ";
  const subTitle = isEn ? "QTSC Smart Facility 360 — Incident Report" : "QTSC Smart Facility 360 — Incident Report";
  const badge = isEn ? "✅ RESOLVED" : "✅ ĐÃ XỬ LÝ — RESOLVED";
  const infoHeader = isEn ? "General Information" : "Thông tin chung";
  const idLabel = isEn ? "Report ID:" : "Mã biên bản:";
  const timeLabel = isEn ? "Time:" : "Thời gian:";
  const locationLabel = isEn ? "Location:" : "Vị trí:";
  const locationValue = isEn ? "Water supply valve area — Building A, QTSC" : "Khu vực van cấp nước — Tòa A, QTSC";
  const reporterLabel = isEn ? "Reported by:" : "Người báo cáo:";
  const reporterValue = isEn ? "Field Technician" : "Kỹ thuật viên hiện trường";
  const supportLabel = isEn ? "Supported by:" : "Hỗ trợ bởi:";
  const descHeader = isEn ? "Incident Description" : "Mô tả sự cố";
  const typeLabel = isEn ? "Type:" : "Loại sự cố:";
  const typeValue = isEn ? "Valve jammed — Pressure leak" : "Van bị kẹt — Rò rỉ áp suất";
  const severityLabel = isEn ? "Severity:" : "Mức độ:";
  const severityValue = isEn ? "Medium" : "Trung bình (Medium)";
  const descLabel = isEn ? "Description:" : "Mô tả:";
  const actionHeader = isEn ? "Corrective Action" : "Biện pháp xử lý";
  const sopLabel = isEn ? "Procedure:" : "Quy trình:";
  const stepsLabel = isEn ? "Steps:" : "Các bước:";
  const stepsValue = isEn ? "Safety pin → Turn CW → Check pressure → Lock valve" : "Chốt an toàn → Vặn CW → Kiểm tra áp suất → Khóa van";
  const resultLabel = isEn ? "Result:" : "Kết quả:";
  const resultValue = isEn ? "Incident controlled. Pressure = 0 bar." : "Sự cố đã được kiểm soát. Áp suất = 0 bar.";
  const statusLabel = isEn ? "Status:" : "Trạng thái:";
  const statusValue = isEn ? "✅ Resolved" : "✅ Đã xử lý";
  const sigExec = isEn ? "Executed by" : "Người thực hiện";
  const sigSuper = isEn ? "Shift Supervisor" : "Trưởng ca xác nhận";
  const footerText = isEn ? `Auto-generated by AI ${AGENT_NAME} — QTSC Smart Facility 360` : `Tự động tạo bởi AI ${AGENT_NAME} — QTSC Smart Facility 360`;

  const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"/><title>Incident Report</title>
<style>
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:'Segoe UI',Arial,sans-serif;padding:40px;color:#1a1a1a}
.header{text-align:center;margin-bottom:28px;border-bottom:3px solid #2563eb;padding-bottom:14px}
.header h1{font-size:20px;color:#2563eb}
.header p{font-size:11px;color:#666;margin-top:4px}
.badge{display:inline-block;background:#22c55e;color:#fff;padding:4px 12px;border-radius:16px;font-size:12px;font-weight:600;margin-top:6px}
.section{margin-bottom:18px}
.section h3{font-size:13px;color:#2563eb;margin-bottom:6px;text-transform:uppercase;letter-spacing:1px}
.row{display:flex;border-bottom:1px solid #e5e7eb;padding:6px 0}
.label{width:160px;font-weight:600;color:#374151;font-size:12px}
.value{flex:1;color:#1a1a1a;font-size:12px}
.footer{margin-top:32px;text-align:center;font-size:10px;color:#9ca3af;border-top:1px solid #e5e7eb;padding-top:12px}
.sig-area{margin-top:40px;display:flex;justify-content:space-between}
.sig-box{width:180px;text-align:center}
.sig-line{border-top:1px solid #374151;margin-top:40px;padding-top:4px;font-size:11px;color:#374151}
</style></head><body>
<div class="header">
  <h1>${title}</h1>
  <p>${subTitle}</p>
  <span class="badge">${badge}</span>
</div>
<div class="section"><h3>${infoHeader}</h3>
  <div class="row"><span class="label">${idLabel}</span><span class="value">INC-${Date.now().toString(36).toUpperCase()}</span></div>
  <div class="row"><span class="label">${timeLabel}</span><span class="value">${dateStr}</span></div>
  <div class="row"><span class="label">${locationLabel}</span><span class="value">${locationValue}</span></div>
  <div class="row"><span class="label">${reporterLabel}</span><span class="value">${reporterValue}</span></div>
  <div class="row"><span class="label">${supportLabel}</span><span class="value">AI ${AGENT_NAME}</span></div>
</div>
<div class="section"><h3>${descHeader}</h3>
  <div class="row"><span class="label">${typeLabel}</span><span class="value">${typeValue}</span></div>
  <div class="row"><span class="label">${severityLabel}</span><span class="value">${severityValue}</span></div>
  <div class="row"><span class="label">${descLabel}</span><span class="value">${escapeHtml(report.summary)}</span></div>
</div>
<div class="section"><h3>${actionHeader}</h3>
  <div class="row"><span class="label">${sopLabel}</span><span class="value">SOP-VLV-003</span></div>
  <div class="row"><span class="label">${stepsLabel}</span><span class="value">${stepsValue}</span></div>
  <div class="row"><span class="label">${resultLabel}</span><span class="value">${resultValue}</span></div>
  <div class="row"><span class="label">${statusLabel}</span><span class="value" style="color:#16a34a;font-weight:700">${statusValue}</span></div>
</div>
<div class="sig-area">
  <div class="sig-box"><div class="sig-line">${sigExec}</div></div>
  <div class="sig-box"><div class="sig-line">${sigSuper}</div></div>
</div>
<div class="footer">${footerText}<br/>${dateStr}</div>
</body></html>`;

  return new Blob([html], { type: 'text/html' });
}

function downloadIncidentPdf(report: IncidentReportData) {
  const blob = generateIncidentPdfBlob(report);
  const url = URL.createObjectURL(blob);
  const printWindow = window.open(url, '_blank');
  if (printWindow) {
    printWindow.onload = () => printWindow.print();
  }
  const a = document.createElement('a');
  a.href = url;
  a.download = report.fileName.replace('.pdf', '.html');
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

// ==================== COMPONENT ====================

export default function FacilityDemoOverlay() {
  const [state, setState] = useState<OverlayState>('hidden');
  const [facilityData, setFacilityData] = useState<FacilityDemoData>(DEFAULT_FACILITY_DATA);
  const [reportData, setReportData] = useState<IncidentReportData | null>(null);
  const [activeCard, setActiveCard] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // ---- Event Listeners ----
  useEffect(() => {
    const handleShowCards = (event: CustomEvent) => {
      const d = event.detail || {};
      setFacilityData({
        equipmentName: d.equipmentName || DEFAULT_FACILITY_DATA.equipmentName,
        videoUrl: d.videoUrl || DEMO_VIDEO_URL,
        videoTitle: d.videoTitle || DEFAULT_FACILITY_DATA.videoTitle,
        sopTitle: d.sopTitle || DEFAULT_FACILITY_DATA.sopTitle,
        sopLines: d.sopLines || DEFAULT_SOP_LINES,
        modelOpened: !!d.modelOpened || !!d.modelData,
      });
      setActiveCard(0);
      setState('cards');
    };

    const handleResolve = (event: CustomEvent) => {
      const d = event.detail || {};
      const now = new Date();
      const ds = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
      const lang = String(d.language || 'vi').toLowerCase();
      const isEn = lang === 'en' || lang === 'english';
      setReportData({
        summary: d.summary || (isEn ? 'Water valve jammed — manually closed following SOP-VLV-003.' : 'Van cấp nước bị kẹt — đã đóng van thủ công theo SOP-VLV-003.'),
        timestamp: d.timestamp || now.toISOString(),
        fileName: d.fileName || `Report_${ds}_Incident.pdf`,
        language: lang,
      });
      setState('transitioning');
      setTimeout(() => setState('resolved'), 500);
    };

    const handleClose = () => {
      setState('hidden');
      setReportData(null);
    };

    window.addEventListener('show_facility_demo_cards', handleShowCards as EventListener);
    window.addEventListener('resolve_incident_generate_report', handleResolve as EventListener);
    window.addEventListener('close_facility_demo', handleClose as EventListener);
    return () => {
      window.removeEventListener('show_facility_demo_cards', handleShowCards as EventListener);
      window.removeEventListener('resolve_incident_generate_report', handleResolve as EventListener);
      window.removeEventListener('close_facility_demo', handleClose as EventListener);
    };
  }, []);

  // Auto-play video on card 3
  useEffect(() => {
    if (state === 'cards' && activeCard === 2 && videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  }, [state, activeCard]);

  // Scroll to card
  const scrollToCard = useCallback((index: number) => {
    setActiveCard(index);
    const container = scrollRef.current;
    if (!container || !container.children[index]) return;
    (container.children[index] as HTMLElement).scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'start',
    });
  }, []);

  // Detect scroll position to update dots
  useEffect(() => {
    const container = scrollRef.current;
    if (!container || state !== 'cards') return;
    let timeout: ReturnType<typeof setTimeout>;
    const onScroll = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        const w = container.offsetWidth;
        if (w > 0) {
          const idx = Math.round(container.scrollLeft / w);
          setActiveCard(Math.min(Math.max(idx, 0), 2));
        }
      }, 80);
    };
    container.addEventListener('scroll', onScroll, { passive: true });
    return () => container.removeEventListener('scroll', onScroll);
  }, [state]);

  if (state === 'hidden') return null;

  const CARD_LABELS = [
    { icon: '🏗️', label: 'MODEL 3D' },
    { icon: '📄', label: 'SOP DOCUMENT' },
    { icon: '🎥', label: 'VIDEO HƯỚNG DẪN' },
  ];

  return (
    <>
      <style>{KEYFRAMES_CSS}</style>

      {/* Fixed full-screen backdrop */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(0,0,0,0.6)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          animation: 'fdoFadeIn 300ms ease-out both',
        }}
      >
        {/* ==================== CARDS STATE ==================== */}
        {(state === 'cards' || state === 'transitioning') && (
          <div
            style={{
              width: '100%',
              maxWidth: '960px',
              margin: '0 auto',
              padding: '16px',
              opacity: state === 'transitioning' ? 0 : 1,
              transform: state === 'transitioning' ? 'scale(0.92)' : 'scale(1)',
              transition: 'opacity 400ms, transform 400ms',
            }}
          >
            {/* Header */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              marginBottom: '16px', padding: '0 4px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '32px' }}>🔧</span>
                <div>
                  <div style={{ color: '#fff', fontSize: '18px', fontWeight: 700 }}>
                    {facilityData.equipmentName}
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>
                    AI {AGENT_NAME} • Kho tư liệu Media & AI Coaching
                  </div>
                </div>
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {/* Alert badge */}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.35)',
                  borderRadius: '24px', padding: '8px 16px',
                  color: '#fca5a5', fontSize: '13px', fontWeight: 600,
                }}>
                  <span style={{
                    width: '10px', height: '10px', borderRadius: '50%', background: '#ef4444',
                    animation: 'fdoAlertPulse 1.5s ease-in-out infinite', display: 'inline-block',
                  }} />
                  Đang xử lý sự cố
                </div>
                
                {/* Close Button */}
                <button
                  onClick={() => setState('hidden')}
                  style={{
                    width: '36px', height: '36px', borderRadius: '50%',
                    background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)',
                    color: '#fff', fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Dot navigation (above cards for mobile visibility) */}
            <div style={{
              display: 'flex', justifyContent: 'center', gap: '10px',
              marginBottom: '14px',
            }}>
              {CARD_LABELS.map((c, i) => (
                <button
                  key={i}
                  onClick={() => scrollToCard(i)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    background: activeCard === i ? 'rgba(59,130,246,0.25)' : 'rgba(255,255,255,0.08)',
                    border: activeCard === i ? '1px solid rgba(59,130,246,0.5)' : '1px solid rgba(255,255,255,0.12)',
                    borderRadius: '20px', padding: '6px 14px',
                    color: activeCard === i ? '#93c5fd' : 'rgba(255,255,255,0.45)',
                    fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                    transition: 'all 0.25s',
                  }}
                >
                  <span>{c.icon}</span> {c.label}
                </button>
              ))}
            </div>

            {/* Cards horizontal scroll — one card at a time */}
            <div
              ref={scrollRef}
              style={{
                display: 'flex', gap: '0px',
                overflowX: 'auto', scrollSnapType: 'x mandatory',
                WebkitOverflowScrolling: 'touch',
                borderRadius: '20px',
                scrollbarWidth: 'none',
              }}
            >
              {/* Card 1: 3D Model */}
              <div style={cardWrapperStyle} className="fdo-card-enter">
                <div style={cardInnerStyle}>
                  <div style={{
                    flex: 1, display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center', gap: '16px',
                    padding: '24px',
                  }}>
                    <div style={{
                      width: '80px', height: '80px', borderRadius: '50%',
                      background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '40px', boxShadow: '0 8px 32px rgba(59,130,246,0.3)',
                    }}>
                      🏗️
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ color: '#fff', fontSize: '20px', fontWeight: 700 }}>
                        Model 3D
                      </div>
                      <div style={{
                        color: 'rgba(255,255,255,0.55)', fontSize: '14px', marginTop: '6px',
                        maxWidth: '280px',
                      }}>
                        {facilityData.equipmentName}
                      </div>
                      <div style={{
                        marginTop: '12px',
                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                        background: facilityData.modelOpened ? 'rgba(34,197,94,0.15)' : 'rgba(59,130,246,0.15)',
                        border: facilityData.modelOpened ? '1px solid rgba(34,197,94,0.3)' : '1px solid rgba(59,130,246,0.3)',
                        borderRadius: '20px', padding: '6px 16px',
                        color: facilityData.modelOpened ? '#86efac' : '#93c5fd',
                        fontSize: '13px', fontWeight: 600,
                      }}>
                        {facilityData.modelOpened ? '✅ Đã mở Model Viewer' : '🔄 Đang tải Model 3D...'}
                      </div>
                    </div>
                    <div style={{
                      color: 'rgba(255,255,255,0.3)', fontSize: '12px', marginTop: '8px',
                    }}>
                      ← Vuốt để xem SOP & Video →
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 2: SOP Document */}
              <div style={cardWrapperStyle} className="fdo-card-enter" data-delay="150">
                <div style={cardInnerStyle}>
                  <div style={{
                    padding: '20px', display: 'flex', flexDirection: 'column', height: '100%',
                  }}>
                    {/* SOP Header */}
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px',
                    }}>
                      <span style={{
                        width: '36px', height: '36px', borderRadius: '10px',
                        background: 'rgba(251,191,36,0.15)', display: 'flex',
                        alignItems: 'center', justifyContent: 'center', fontSize: '20px',
                      }}>📋</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ color: '#fbbf24', fontSize: '11px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase' as const }}>
                          SOP Document
                        </div>
                        <div style={{ color: '#fff', fontSize: '14px', fontWeight: 600, marginTop: '2px' }}>
                          {facilityData.sopTitle}
                        </div>
                      </div>
                    </div>
                    <div style={{ height: '1px', background: 'rgba(255,255,255,0.1)', marginBottom: '12px' }} />
                    {/* SOP Lines */}
                    <div style={{
                      flex: 1, overflowY: 'auto', display: 'flex',
                      flexDirection: 'column', gap: '6px',
                    }}>
                      {facilityData.sopLines.map((line, i) => (
                        <div
                          key={i}
                          style={{
                            padding: '10px 14px', borderRadius: '10px', fontSize: '14px',
                            lineHeight: '1.6', transition: 'all 0.3s',
                            ...(line.highlight
                              ? {
                                  background: 'rgba(251,191,36,0.15)',
                                  border: '1px solid rgba(251,191,36,0.4)',
                                  color: '#fbbf24', fontWeight: 700,
                                }
                              : {
                                  background: 'rgba(255,255,255,0.04)',
                                  border: '1px solid transparent',
                                  color: 'rgba(255,255,255,0.8)',
                                }),
                          }}
                        >
                          {line.text}
                        </div>
                      ))}
                    </div>
                    <div style={{
                      marginTop: '10px', color: 'rgba(255,255,255,0.25)', fontSize: '11px',
                      textAlign: 'right',
                    }}>
                      Phê duyệt: Trưởng phòng Kỹ thuật • Rev 3.2
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 3: Video */}
              <div style={cardWrapperStyle} className="fdo-card-enter" data-delay="300">
                <div style={cardInnerStyle}>
                  <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <video
                      ref={videoRef}
                      src={facilityData.videoUrl}
                      style={{
                        width: '100%', flex: 1, objectFit: 'cover',
                        borderRadius: '16px 16px 0 0', background: '#000',
                        minHeight: '0',
                      }}
                      controls
                      autoPlay
                      muted
                      playsInline
                      loop
                    />
                    <div style={{ padding: '14px 18px' }}>
                      <div style={{ color: '#fff', fontSize: '16px', fontWeight: 600 }}>
                        {facilityData.videoTitle}
                      </div>
                      <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '13px', marginTop: '4px' }}>
                        Auto-play • Hướng dẫn thao tác
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ==================== RESOLVED STATE ==================== */}
        {state === 'resolved' && reportData && (
          <div style={{
            width: '100%', maxWidth: '520px', margin: '0 auto', padding: '20px',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px',
            animation: 'fdoResolvedEnter 500ms cubic-bezier(0.16,1,0.3,1) both',
            position: 'relative',
          }}>
            {/* Close Button */}
            <button
              onClick={() => setState('hidden')}
              style={{
                position: 'absolute', top: '10px', right: '10px',
                width: '32px', height: '32px', borderRadius: '50%',
                background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(255,255,255,0.2)',
                color: '#fff', fontSize: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', zIndex: 10, transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.5)'}
            >
              ✕
            </button>

            {/* Resolved badge */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: '16px', width: '100%',
              background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(20px)',
              borderRadius: '20px', border: '1px solid rgba(34,197,94,0.35)',
              padding: '20px 24px',
              animation: 'fdoCheckBounce 700ms cubic-bezier(0.16,1,0.3,1) both',
            }}>
              <span style={{ fontSize: '44px' }}>✅</span>
              <div>
                <div style={{ color: '#22c55e', fontSize: '18px', fontWeight: 700, letterSpacing: '0.5px' }}>
                  {(reportData.language === 'en' || reportData.language === 'english') ? "INCIDENT RESOLVED" : "SỰ CỐ ĐÃ ĐƯỢC XỬ LÝ"}
                </div>
                <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', marginTop: '4px' }}>
                  {(reportData.language === 'en' || reportData.language === 'english') ? "Water supply valve system" : facilityData.equipmentName} — Resolved
                </div>
              </div>
            </div>

            {/* Report card */}
            <div style={{
              width: '100%', background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(20px)',
              borderRadius: '20px', border: '1px solid rgba(255,255,255,0.12)',
              padding: '24px', boxShadow: '0 25px 60px rgba(0,0,0,0.5)',
              animation: 'fdoReportSlideUp 600ms cubic-bezier(0.16,1,0.3,1) 300ms both',
            }}>
              {/* Header */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: '12px',
                marginBottom: '18px', paddingBottom: '14px',
                borderBottom: '1px solid rgba(255,255,255,0.1)',
              }}>
                <span style={{ fontSize: '24px' }}>📋</span>
                <span style={{ color: '#fff', fontSize: '17px', fontWeight: 700 }}>
                  {(reportData.language === 'en' || reportData.language === 'english') ? "Incident Report" : "Biên bản báo cáo sự cố"}
                </span>
              </div>

              {/* Report rows */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '22px' }}>
                {[
                  [(reportData.language === 'en' || reportData.language === 'english') ? 'Time' : 'Thời gian', new Date(reportData.timestamp).toLocaleString((reportData.language === 'en' || reportData.language === 'english') ? 'en-US' : 'vi-VN')],
                  [(reportData.language === 'en' || reportData.language === 'english') ? 'Location' : 'Vị trí', (reportData.language === 'en' || reportData.language === 'english') ? 'Water valve area — Building A' : 'Khu vực van cấp nước — Tòa A'],
                  [(reportData.language === 'en' || reportData.language === 'english') ? 'Incident' : 'Sự cố', (reportData.language === 'en' || reportData.language === 'english') ? 'Valve jammed, pressure leak' : 'Van bị kẹt, rò rỉ áp suất'],
                  [(reportData.language === 'en' || reportData.language === 'english') ? 'Action' : 'Xử lý', (reportData.language === 'en' || reportData.language === 'english') ? 'Closed valve via SOP-VLV-003' : 'Đóng van thủ công theo SOP-VLV-003'],
                ].map(([label, value], i) => (
                  <div key={i} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                    <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: '14px', minWidth: '90px', flexShrink: 0 }}>
                      {label}:
                    </span>
                    <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '14px' }}>
                      {value}
                    </span>
                  </div>
                ))}
                <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                  <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: '14px', minWidth: '90px', flexShrink: 0 }}>
                    {(reportData.language === 'en' || reportData.language === 'english') ? "Status:" : "Trạng thái:"}
                  </span>
                  <span style={{ color: '#22c55e', fontSize: '14px', fontWeight: 700 }}>
                    {(reportData.language === 'en' || reportData.language === 'english') ? "✅ Resolved" : "✅ Đã xử lý"}
                  </span>
                </div>
              </div>

              {/* Download button */}
              <button
                onClick={() => downloadIncidentPdf(reportData)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px',
                  width: '100%', padding: '16px 20px',
                  background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                  border: 'none', borderRadius: '14px', color: '#fff',
                  fontSize: '16px', fontWeight: 700, cursor: 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: '0 4px 20px rgba(37,99,235,0.35)',
                  animation: 'fdoDownloadPulse 2s ease-in-out infinite',
                }}
              >
                <span style={{ fontSize: '20px' }}>📥</span>
                Download {reportData.fileName}
              </button>
            </div>

            <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: '12px' }}>
              {(reportData.language === 'en' || reportData.language === 'english') ? `🤖 Auto-generated by AI ${AGENT_NAME}` : `🤖 Tự động tạo bởi AI ${AGENT_NAME}`}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// ==================== SHARED CARD STYLES ====================

const cardWrapperStyle: React.CSSProperties = {
  flex: '0 0 100%',
  width: '100%',
  scrollSnapAlign: 'start',
  padding: '0 8px',
  boxSizing: 'border-box',
};

const cardInnerStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.06)',
  borderRadius: '20px',
  border: '1px solid rgba(255,255,255,0.12)',
  height: 'clamp(320px, 50vh, 480px)',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
};

// ==================== KEYFRAMES ====================

const KEYFRAMES_CSS = `
@keyframes fdoFadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
@keyframes fdoCardEnter {
  from { opacity: 0; transform: translateX(60px); }
  to { opacity: 1; transform: translateX(0); }
}
@keyframes fdoResolvedEnter {
  from { opacity: 0; transform: scale(0.9); }
  to { opacity: 1; transform: scale(1); }
}
@keyframes fdoCheckBounce {
  0% { transform: scale(0); opacity: 0; }
  50% { transform: scale(1.08); }
  70% { transform: scale(0.96); }
  100% { transform: scale(1); opacity: 1; }
}
@keyframes fdoReportSlideUp {
  from { opacity: 0; transform: translateY(40px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes fdoDownloadPulse {
  0%, 100% { box-shadow: 0 4px 20px rgba(37,99,235,0.35); }
  50% { box-shadow: 0 4px 30px rgba(37,99,235,0.6); }
}
@keyframes fdoAlertPulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}
.fdo-card-enter {
  animation: fdoCardEnter 500ms cubic-bezier(0.16,1,0.3,1) both;
}
.fdo-card-enter[data-delay="150"] {
  animation-delay: 150ms;
}
.fdo-card-enter[data-delay="300"] {
  animation-delay: 300ms;
}
/* Hide scrollbar for card container */
div::-webkit-scrollbar {
  display: none;
}
`;
